import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Icon } from '@codex-brightworld/icons';
import {
  Button, Input, Link, VerticalScrollView
} from '@codex-brightworld/basics';

const AddressFinderWrapper = styled.div`
  position: relative;
`;

const AddressesResults = styled.div`
  ${({ theme }) => css`
    border: solid 1px ${theme.colors.shades[5]};
    background: ${theme.colors.shades[7]};
    margin-top: ${theme.spacing[2]};
    position: absolute;
    width: 100%;
    z-index: 4;
  `};
`;

const AddressesList = styled.ul`
  display: block;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AddressesOverlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const AddressesItem = styled.li`
  height: 39px;
  &:last-of-type ${Link} {
    border-bottom: none;
  }
`;

const AddressButton = styled(Button)`
  ${({ theme }) => css`
    display: inline-block;
    width: calc(50% - ${theme.spacing[1]});
    max-width: 320px;
    float: right;
    position: relative;
  `};
`;

const SpinnerIcon = styled(Icon).attrs({ icon: 'loading', spin: true })`
  margin: -24px 0 -24px 4px;
  width: 24px;
  height: 24px;
  stroke: #999;
`;

const AddressInput = styled(Input)`
  ${({ theme }) => css`
    display: inline-block;
    width: calc(50% - ${theme.spacing[1]});
  `};
`;

const AddressLink = styled(Link)`
  ${({ theme }) => css`
    border-bottom: solid 1px ${theme.colors.shades[5]};
    color: ${theme.colors.shades[1]};
    display: inline-block;
    font-size: ${theme.fonts.default.fontSize};
    line-height: 38px;
    overflow: hidden;
    padding: 0 ${theme.spacing[4]};
    text-overflow: ellipsis;
    user-select: none;
    white-space: nowrap;
    width: 100%;
    &:hover{
      text-decoration: none;
      background:  ${theme.colors.message.hover};
    }
  `};
`;

const AddressesEnd = styled.div`
  ${({ theme, addressesShown }) => css`
    font-size: ${theme.fonts.default.fontSize};
    line-height: 38px;
    padding: 1px ${theme.spacing[4]} 0;
    position: relative;
    text-align: center;
    ${addressesShown && `border-top: solid 1px ${theme.colors.shades[5]}`}
  `};
`;

const AddressFinder = props => (
  <AddressFinderWrapper className={props.className} data-qa={props.qa}>
    {props.renderInput(AddressInput, props)}
    {props.renderButton(AddressButton, props)}
    {props.visibleResults && props.renderAddressesList(props)}
  </AddressFinderWrapper>
);

AddressFinder.displayName = 'AddressFinder';

AddressFinder.propTypes = {
  addresses: PropTypes.arrayOf(PropTypes.shape({
    company: PropTypes.string,
    county: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    postCode: PropTypes.string,
    town: PropTypes.string
  })),
  addressNotListedLabel: PropTypes.string,
  activeSearching: PropTypes.bool.isRequired,
  analyticsAttributeName: PropTypes.string,
  analyticsAttributeValue: PropTypes.string,
  buttonLabel: PropTypes.string,
  className: PropTypes.string,
  inputLabel: PropTypes.string,
  name: PropTypes.string,
  onCloseAddresses: PropTypes.func.isRequired,
  onClickAddress: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onInputBlur: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  postCode: PropTypes.shape({
    value: PropTypes.string.isRequired,
    error: PropTypes.string
  }).isRequired,
  /**
   *
   */
  renderAddressesList: PropTypes.func,
  renderButton: PropTypes.func,
  renderInput: PropTypes.func,
  scrollableFrom: PropTypes.number,
  visibleResults: PropTypes.bool.isRequired,
  qa: PropTypes.string
};

AddressFinder.defaultProps = {
  addresses: [],
  addressNotListedLabel: null,
  analyticsAttributeName: null,
  analyticsAttributeValue: null,
  buttonLabel: 'find_address',
  className: '',
  inputLabel: 'zip_code',
  name: 'zip',
  onInputBlur: null,
  renderAddressesList: ({
    /* eslint-disable-next-line react/prop-types */
    addresses, addressNotListedLabel, onClickAddress, onCloseAddresses, scrollableFrom
  }) => (
    <AddressesResults>
      <AddressesOverlay
        onKeyDown={() => { }}
        onClick={() => { onCloseAddresses(); }}
      />
      {addresses.length > 0 && (
        <VerticalScrollView
          // because of related position of scrollbar in the component (top:4px bottom: 4px)
          scrollbarCorrection={8}
          scrollEnabled={addresses.length > scrollableFrom}
        >
          <AddressesList>
            {addresses.map((addrItem, i) => {
              const addressLine = [
                addrItem.company,
                addrItem.line1,
                addrItem.line2,
                addrItem.town,
                addrItem.postCode
              ].filter(item => item).join(', ');
              return (
                <AddressesItem key={`${i}`}>
                  <AddressLink onClick={() => onClickAddress(addrItem)}>
                    {addressLine}
                  </AddressLink>
                </AddressesItem>
              );
            })}
          </AddressesList>
        </VerticalScrollView>
      )}
      {addressNotListedLabel && (
        <AddressesEnd addressesShown={!!addresses.length}>
          <Link onClick={() => onCloseAddresses()}>
            {addressNotListedLabel}
          </Link>
        </AddressesEnd>
      )}
    </AddressesResults>
  ),

  renderButton: (DefaultButton, { activeSearching, buttonLabel, onSubmit }) => (
    <DefaultButton
      disabled={activeSearching}
      styleType="primary"
      onClick={() => onSubmit()}
      type="button"
      primary
    >
      {buttonLabel} {activeSearching && <SpinnerIcon />}
    </DefaultButton>
  ),

  renderInput: (DefaultInput, {
    analyticsAttributeName,
    analyticsAttributeValue,
    inputLabel,
    name,
    onInputChange,
    onInputBlur,
    postCode
  }) => (
    <DefaultInput
      valid={!postCode.error}
      interactivePlaceholder
      placeholderText={inputLabel}
      name={name}
      value={postCode.value}
      onChange={onInputChange}
      onBlur={onInputBlur}
      analyticsAttributeName={analyticsAttributeName}
      analyticsAttributeValue={analyticsAttributeValue}
    />
  ),

  scrollableFrom: 5,
  qa: undefined
};

export default AddressFinder;
